import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  VisibilityOutlined as VisibilityIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  SmartDisplayOutlined as VideoIcon,
  DescriptionOutlined as PdfIcon,
  CategoryOutlined as CategoryIcon,
  LocalOfferOutlined as TagIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useCompany} from "../../contexts/CompanyContext";
import {FilterDropdown, Pagination} from "../ui";
import {t} from "../dashboard/coach/coachTokens";
import TabsRow from "../dashboard/coach/TabsRow";
import ResourceCategoryManager from "./ResourceCategoryManager";
import ResourceTagManager from "./ResourceTagManager";
import {RTL} from "../../cache/CacheRtl";
import ResourceDialog from "./ResourceDialog";
import DeleteResourceDialog from "./DeleteResourceDialog";
import VideoPlayerModal from "../shared/VideoPlayerModal";
import moment from "moment";

/* ── Constants ───────────────────────────────────────────── */
const PAGE_SIZE = 8;

/* ── Table cell presets (match Clients / Exercises) ──────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   Generic resource library page (Guides / Recipes / …)
   Driven entirely by `config` — see guidesConfig.js / recipesConfig.js
   ═══════════════════════════════════════════════════════════ */
const ResourceLibraryScreen = ({config}) => {
  const {service, labels, mainIcon: MainIcon} = config;
  const navigate = useNavigate();
  const location = useLocation();
  const {company} = useCompany();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const TABS = useMemo(() => [
    {key: "items", label: labels.tabMain, Icon: MainIcon},
    {key: "categories", label: labels.tabCategories, Icon: CategoryIcon},
    {key: "tags", label: labels.tabTags, Icon: TagIcon},
  ], [labels.tabMain, labels.tabCategories, labels.tabTags, MainIcon]);

  const [activeTab, setActiveTab] = useState("items");
  const categoryManagerRef = useRef(null);
  const tagManagerRef = useRef(null);

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [dialogMode, setDialogMode] = useState(null); // "view" | "edit" | "delete" | "video"

  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const [f, setF] = useState({
    items: [],
    total: 0,
    totalPages: 0,
    keyword: query.get("keyword") || "",
    page: +(query.get("page") || 0),
    loading: true,
  });

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL sync
  const sync = (ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.page > 0) p.set("page", ns.page);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  };

  const set = (patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); };

  const load = () => {
    setF(p => ({...p, loading: true}));
    const tagIds = selectedTags.map(tg => tg.id);
    service.getList(f.page, PAGE_SIZE, f.keyword, company?.id, selectedCategoryId || null, tagIds)
        .then(r => setF(p => ({
          ...p,
          items: r.data?.content || [],
          total: r.data?.totalElements || 0,
          totalPages: r.data?.totalPages || 0,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
  };

  // Fetch items
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.page, f.keyword, company, selectedCategoryId, selectedTags]);

  // Fetch the tag list for the filter (all company tags).
  useEffect(() => {
    const tagService = config.tagService;
    if (!tagService || !company?.id) { setTags([]); return; }
    tagService.getTags(company.id)
        .then(r => setTags(r.data || []))
        .catch(() => setTags([]));
  }, [config.tagService, company]);

  // Fetch the category list for the filter on mount — mirrors the tags effect above.
  // Without this, `categories` was only ever populated by ResourceCategoryManager's
  // onCategoriesChange, and the manager mounts only on the Categories tab; so on the
  // default Items tab the category FilterDropdown (gated on categories.length > 0) never
  // rendered until the user visited Categories and came back. onCategoriesChange stays
  // wired, so edits made in the Categories tab still refresh this list.
  useEffect(() => {
    const categoryService = config.categoryService;
    if (!categoryService || !company?.id) { setCategories([]); return; }
    categoryService.getCategories(company.id)
        .then(r => setCategories(r.data || []))
        .catch(() => setCategories([]));
  }, [config.categoryService, company]);

  const reload = () => load();

  const pages = Math.ceil(f.total / PAGE_SIZE);

  // Dialog helpers
  const openDialog = (item, mode) => (e) => {
    if (e) e.stopPropagation();
    setActiveItem(item);
    setDialogMode(mode);
  };
  const closeDialog = () => { setActiveItem(null); setDialogMode(null); };

  const handleDelete = async () => {
    if (!activeItem) return;
    try {
      await service.remove(activeItem.id);
      closeDialog();
      reload();
    } catch (error) {
      console.error("Error deleting resource:", error);
    }
  };

  const openPdf = (item) => (e) => {
    e.stopPropagation();
    if (item.pdfUrl) window.open(item.pdfUrl, "_blank");
  };

  const handleAdd = () => {
    if (activeTab === "items") setCreateDialogOpen(true);
    else if (activeTab === "categories") categoryManagerRef.current?.openCreate();
    else if (activeTab === "tags") tagManagerRef.current?.openCreate();
  };

  const addButtonLabel =
    activeTab === "items" ? labels.newItemButton
      : activeTab === "tags" ? labels.newTagButton
      : labels.newCategoryButton;

  const CATEGORY_OPTS = [{value: "ALL", label: labels.allCategories}, ...categories.map(c => ({value: c.id, label: c.name}))];

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
        {/* Create dialog */}
        <ResourceDialog config={config} isOpen={createDialogOpen} close={() => setCreateDialogOpen(false)} setReload={reload}/>

        {/* View/Edit dialog */}
        {activeItem && (dialogMode === "view" || dialogMode === "edit") && (
            <ResourceDialog
                config={config}
                key={`${dialogMode}-${activeItem.id}-${activeItem.thumbnailUrl || "no-thumb"}`}
                isOpen={true}
                close={closeDialog}
                setReload={reload}
                editItem={activeItem}
                viewMode={dialogMode === "view"}
            />
        )}

        {/* Delete dialog */}
        <DeleteResourceDialog open={dialogMode === "delete"} handleClose={closeDialog} handleDelete={handleDelete} confirmText={labels.deleteConfirmText}/>

        {/* Video player */}
        {activeItem && dialogMode === "video" && (
            <VideoPlayerModal isOpen={true} close={closeDialog} videoUrl={activeItem.videoUrl}
                              thumbnailUrl={activeItem.thumbnailUrl} title={activeItem.title}/>
        )}

        {/* ─── Title row (on canvas) ──────────────────────── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              {labels.pageTitle}
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>{labels.pageSubtitle}</Typography>
          </Box>
          <Box component="button" onClick={handleAdd} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff", height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            {addButtonLabel}
          </Box>
        </Box>

        {/* ─── Tabs ────────────────────────────────────────── */}
        <TabsRow value={activeTab} onChange={setActiveTab} tabs={TABS}/>

        {/* ─── Items tab ───────────────────────────────────── */}
        {activeTab === "items" && (
            <Box sx={{background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh, overflow: "hidden", display: "flex", flexDirection: "column"}}>

              {/* Filter bar */}
              <Box sx={{
                display: "flex", alignItems: "center", gap: "7px",
                padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
                background: t.bg, flexWrap: "wrap", flexShrink: 0, direction: "rtl",
              }}>
                <Box sx={{
                  display: "inline-flex", alignItems: "center", gap: "4px",
                  fontSize: "12px", fontWeight: 700, color: t.greenText,
                  background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
                  padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
                }}>
                  <MainIcon sx={{fontSize: 14}}/>
                  {f.total} {labels.countSuffix}
                </Box>
                <Box sx={{position: "relative"}}>
                  <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
                  <InputBase
                      value={searchInput}
                      onChange={e => handleSearch(e.target.value)}
                      placeholder={labels.searchPlaceholder}
                      sx={{
                        width: 220, padding: "6px 10px 6px 30px",
                        border: `1.5px solid ${t.border}`, borderRadius: t.rs,
                        background: t.white, color: t.text, fontSize: "12px",
                        "& input": {padding: 0},
                        "& input::placeholder": {color: t.t3, opacity: 1},
                        "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                      }}
                  />
                </Box>
                {categories.length > 0 && (
                    <FilterDropdown label="קטגוריה" value={selectedCategoryId || "ALL"}
                                    onChange={v => { setSelectedCategoryId(v === "ALL" ? "" : v); set({page: 0}); }}
                                    options={CATEGORY_OPTS} minWidth={150}/>
                )}
                {tags.length > 0 && (
                    <RTL>
                      <Autocomplete
                          multiple
                          size="small"
                          options={tags}
                          value={selectedTags}
                          onChange={(_, newValue) => { setSelectedTags(newValue); set({page: 0}); }}
                          getOptionLabel={(option) => option.name || ""}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          sx={{minWidth: 200, maxWidth: 340, direction: "rtl", background: t.white, borderRadius: t.rs}}
                          renderTags={(value, getTagProps) => (
                              <RTL>
                                {value.map((option, index) => (
                                    <Chip
                                        {...getTagProps({index})}
                                        key={option.id}
                                        label={option.name}
                                        size="small"
                                        sx={{backgroundColor: "rgba(1,187,167,.12)", color: t.greenText}}
                                    />
                                ))}
                              </RTL>
                          )}
                          renderInput={(params) => (
                              <TextField {...params} label={labels.tagsLabel || "תגיות"}
                                         sx={{'& .MuiOutlinedInput-input': {textAlign: "right"}}}/>
                          )}
                      />
                    </RTL>
                )}
              </Box>

              {/* Table area */}
              {f.loading && !f.items.length ? (
                  <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                    <CircularProgress size={32} sx={{color: t.green}}/>
                  </Box>
              ) : !f.items.length ? (
                  <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                    <MainIcon sx={{fontSize: 48, color: t.border}}/>
                    <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>{labels.emptyTitle}</Typography>
                    <Typography sx={{color: t.t3, fontSize: "13px"}}>{labels.emptyHint}</Typography>
                  </Box>
              ) : (
                  <>
                    <TableContainer sx={{overflowY: "auto", maxHeight: 540, direction: "rtl"}}>
                      <Table stickyHeader sx={{tableLayout: "fixed"}}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{...th, textAlign: "right"}}>כותרת</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "13%", display: {xs: "none", md: "table-cell"}}}>קטגוריה</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "16%", display: {xs: "none", md: "table-cell"}}}>תגיות</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "10%", display: {xs: "none", md: "table-cell"}}}>קובץ</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "10%", display: {xs: "none", md: "table-cell"}}}>וידאו</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: "12%", display: {xs: "none", md: "table-cell"}}}>תאריך העלאה</TableCell>
                            <TableCell sx={{...th, textAlign: "center", width: 120}}>פעולות</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {f.items.map(item => (
                              <TableRow key={item.id}
                                        sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}
                                        onClick={openDialog(item, "view")}>
                                {/* Title + Description */}
                                <TableCell sx={{...td, textAlign: "right"}}>
                                  <Typography sx={{fontSize: "12.5px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", mb: item.description ? 0.25 : 0}}>
                                    {item.title}
                                  </Typography>
                                  {item.description && (
                                      <Tooltip title={item.description} placement="top">
                                        <Typography sx={{fontSize: "11.5px", color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "400px"}}>
                                          {item.description}
                                        </Typography>
                                      </Tooltip>
                                  )}
                                </TableCell>

                                {/* Category */}
                                <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                  {item.categoryName ? (
                                      <Box component="span" sx={{
                                        display: "inline-block", fontSize: 11, fontWeight: 600, padding: "2px 10px",
                                        borderRadius: "20px", background: t.bg, color: t.t2, border: `1px solid ${t.border}`, whiteSpace: "nowrap",
                                      }}>
                                        {item.categoryName}
                                      </Box>
                                  ) : <Typography sx={{fontSize: "12.5px", color: t.t3}}>—</Typography>}
                                </TableCell>

                                {/* Tags */}
                                <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                  {item.tags?.length ? (
                                      <Box sx={{display: "flex", flexWrap: "wrap", gap: "4px", justifyContent: "center"}}>
                                        {item.tags.map(tag => (
                                            <Box key={tag.id} component="span" sx={{
                                              display: "inline-block", fontSize: 11, fontWeight: 600, padding: "2px 8px",
                                              borderRadius: "20px", background: "rgba(1,187,167,.10)", color: t.greenText,
                                              border: `1px solid ${t.greenLight}`, whiteSpace: "nowrap",
                                            }}>
                                              {tag.name}
                                            </Box>
                                        ))}
                                      </Box>
                                  ) : <Typography sx={{fontSize: "12.5px", color: t.t3}}>—</Typography>}
                                </TableCell>

                                {/* PDF */}
                                <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                  {item.pdfUrl ? (
                                      <Box onClick={openPdf(item)} sx={{display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer", color: t.t2, "&:hover": {color: t.text, textDecoration: "underline"}}}>
                                        <PdfIcon sx={{fontSize: "18px"}}/>
                                        <Typography sx={{fontSize: "12px", fontWeight: 600}}>PDF</Typography>
                                      </Box>
                                  ) : <Typography sx={{fontSize: "12.5px", color: t.t3}}>—</Typography>}
                                </TableCell>

                                {/* Video */}
                                <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                  {item.videoUrl ? (
                                      <Box onClick={openDialog(item, "video")} sx={{display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer", color: t.t2, "&:hover": {color: t.text, textDecoration: "underline"}}}>
                                        <VideoIcon sx={{fontSize: "18px"}}/>
                                        <Typography sx={{fontSize: "12px", fontWeight: 600}}>צפייה</Typography>
                                      </Box>
                                  ) : <Typography sx={{fontSize: "12.5px", color: t.t3}}>—</Typography>}
                                </TableCell>

                                {/* Upload Date */}
                                <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                                  <Typography sx={{fontSize: "12.5px", fontWeight: 500, color: t.t2}}>
                                    {item.uploadDate ? moment(item.uploadDate).format("DD.MM.YYYY") : "—"}
                                  </Typography>
                                </TableCell>

                                {/* Actions */}
                                <TableCell sx={{...td, textAlign: "center", width: 120}} onClick={(e) => e.stopPropagation()}>
                                  <Box sx={{display: "inline-flex", justifyContent: "center", gap: "2px"}}>
                                    <Tooltip title="צפייה">
                                      <IconButton size="small" onClick={openDialog(item, "view")} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                        <VisibilityIcon fontSize="small"/>
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="עריכה">
                                      <IconButton size="small" onClick={openDialog(item, "edit")} sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                        <EditIcon fontSize="small"/>
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="מחק">
                                      <IconButton size="small" onClick={openDialog(item, "delete")} sx={{color: t.red, "&:hover": {bgcolor: t.redLight}}}>
                                        <DeleteIcon fontSize="small"/>
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {pages > 1 && (
                        <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                    onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                    )}
                  </>
              )}
            </Box>
        )}

        {/* ─── Categories tab ──────────────────────────────── */}
        {activeTab === "categories" && (
            <ResourceCategoryManager ref={categoryManagerRef} config={config} companyId={company?.id} onCategoriesChange={setCategories}/>
        )}

        {/* ─── Tags tab ────────────────────────────────────── */}
        {activeTab === "tags" && (
            <ResourceTagManager ref={tagManagerRef} config={config} companyId={company?.id}/>
        )}
      </Box>
  );
};

export default ResourceLibraryScreen;
